// @ts-nocheck
import { Suspense, lazy } from 'react';
import { Navigate, useRoutes, useLocation } from 'react-router-dom';
// layouts
import MainLayout from '../layouts/main';
import DashboardLayout from '../layouts/dashboard';
import LogoOnlyLayout from '../layouts/LogoOnlyLayout';
// guards
import GuestGuard from '../guards/GuestGuard';
import AuthGuard from '../guards/AuthGuard';
import OnboardingGuard from '../guards/OnboardingGuard';
import RoleBasedGuard from '../guards/RoleBasedGuard';
import CampaignsGuard from '../guards/CampaignsGuard';
// components
import { Icon } from '@iconify/react';

import { useSnackbar } from 'notistack';
import { MIconButton } from '../components/@material-extend';
import closeFill from '@iconify/icons-eva/close-fill';
import LoadingScreen from '../components/LoadingScreen';
import useAuth from '../hooks/useAuth';
import { useState, useEffect } from 'react';
import {userRoles } from '../constants/defaultValues';
import { PATH_MOBILE } from './paths';
import Swms from '../pages/documents/swms';
import Hrwa from '../pages/documents/hrwa';
import HazardousSubstances from '../pages/documents/HazardousSubstances';
import { login, loginOk, loginOff, onlineUsers } from '../socket';
// import Verification from '../pages/verification/index'

// ----------------------------------------------------------------------


const Loadable = (Component) => (props) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { pathname } = useLocation();
  const isDashboard = pathname.includes('/dashboard');

  return (
    <Suspense
      fallback={
        <LoadingScreen
          sx={{
            ...(!isDashboard && {
              top: 0,
              left: 0,
              width: 1,
              zIndex: 9999,
              position: 'fixed'
            })
          }}
        />
      }
    >
      <Component {...props} />
    </Suspense>
  );
};

// Legacy/external invite links point at /register?token=... but the app only
// serves the auth flow under /auth/register. Redirect while preserving the
// query string (the invite token) — a bare <Navigate to> would drop it.
function RegisterRedirect() {
  const { search } = useLocation();
  return <Navigate to={`/auth/register${search}`} replace />;
}

export default function Router() {
  const { user, updateOnlineUsers } = useAuth();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  // Initial render before useAuth resolves a role: fall back to the Action
  // Required checklist (every role has it). The legacy /dashboard/app target
  // was a sibling route that itself redirected here, so an unknown role
  // landing on /dashboard/app caused a no-op redirect loop → empty page.
  var [defaultDashboard, setDefaultDashboard] = useState(<Navigate to="/dashboard/checklist" replace />);
  // console.log('user is: ',user);

  const toast = (type, message)=>{
    enqueueSnackbar(message, {
      variant: type,
      action: (key) => (
        <MIconButton size="small" onClick={() => closeSnackbar(key)}>
          <Icon icon={closeFill} />
        </MIconButton>
      )
    });
  }

  useEffect(() => {
    if(user && user.id){
      updateDashboard(user.role);
    }

    login(processLogin)
    onlineUsers(processOnlineUsers);
    processLogin(loginOk());
    return ()=>{
      loginOff(processLogin);
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const processLogin = (login)=>{
		// console.log("login",login)
		if(login.auth){
      // onlineUsers();
		}
	}

  const processOnlineUsers = async users=>{
    // console.log('got online users: ',users);
    await updateOnlineUsers(users);
  }

  useEffect(() => {
    if(user && user.id){
      updateDashboard(user.role);
    }
  }, [user])

  const updateDashboard = role=>{
    // Non-admin roles land on the visual Overview — but Overview calls
    // /v1/dashboard/summary, which 4xx's ("Complete onboarding before using
    // this feature") until the user finishes onboarding. So before onboarding
    // is complete, send them to the Action Required checklist instead. Admin
    // and storeAdmin keep their dedicated home screens.
    const home = user?.onboardingCompleted ? '/dashboard/overview' : '/dashboard/checklist';
    let dashboard = <Navigate to={home} replace />;
    if(role === userRoles.admin || role === userRoles.superadmin) dashboard = <Navigate to="/dashboard/admin/overview" replace />
    else if(role === 'storeAdmin') dashboard = <Navigate to="/dashboard/storeAdmin" replace />
    else dashboard = <Navigate to={home} replace />
    setDefaultDashboard(dashboard);
  }

  return useRoutes([
    {
      path:'documents',
      children:[
        {
          // Legacy WHS page keyed by the sequential jobsite id. Now
          // login-only: the id is guessable, so serving it unauthenticated
          // let anyone enumerate every site's WHS plan. External sharing
          // goes through the tokenized /documents/site/:publicId below.
          path:'jobsite/:jobsiteId',
          element: (
            <AuthGuard>
              <JobsiteDocuments />
            </AuthGuard>
          )
        },
        {
          // PUBLIC inspector view — Safety Plan + SWMS bundle behind the
          // opaque publicId from a "send docs" email/SMS. No login.
          path:'site/:publicId',
          element: <SharedSiteDocs />
        },
        {
          // PUBLIC too, but behind a named grant: revocable, expiring, and
          // every open is recorded for the site (contractor matrix, row 17).
          path:'site/grant/:token',
          element: <SharedSiteDocs />
        }
      ]
    },

    // Mobile / field PWA screens. /m is the worker's home — the picker that
    // lists their assigned sites + GPS-nearby unassigned sites. Confirm
    // screens require auth; login/register-at-site variants are public so
    // visitors can finish onboarding while keeping the pending sign-on
    // intent in localStorage. QR posters encode the bare /m URL; there's no
    // longer a separate /go landing page.
    {
      path: 'm',
      children: [
        {
          path: '',
          element: (
            <AuthGuard>
              <OnboardingGuard>
                <MobileHome />
              </OnboardingGuard>
            </AuthGuard>
          ),
        },
        { path: 'login/:jobsiteId', element: <MobileLoginAtSite /> },
        { path: 'register/:jobsiteId', element: <MobileRegisterAtSite /> },
        {
          // Onboarding "who do you work for?" step. AuthGuard (must be logged
          // in) but NOT OnboardingGuard — a brand-new worker picks their team
          // BEFORE the rest of onboarding.
          path: 'pick-employer',
          element: (
            <AuthGuard>
              <MobilePickEmployer />
            </AuthGuard>
          ),
        },
        {
          // The check-in path (sign-on, its acknowledge gate, sign-off and
          // switch) opens before onboarding is finished — see
          // canCheckInBeforeOnboarding. The server still judges the documents.
          path: 'sign-on/:jobsiteId',
          element: (
            <AuthGuard>
              <OnboardingGuard allowCheckIn>
                <MobileSignOnConfirm />
              </OnboardingGuard>
            </AuthGuard>
          ),
        },
        {
          path: 'sign-off/:jobsiteId',
          element: (
            <AuthGuard>
              <OnboardingGuard allowCheckIn>
                <MobileSignOffConfirm />
              </OnboardingGuard>
            </AuthGuard>
          ),
        },
        {
          path: 'switch-sites/:jobsiteId',
          element: (
            <AuthGuard>
              <OnboardingGuard allowCheckIn>
                <MobileSwitchSitesConfirm />
              </OnboardingGuard>
            </AuthGuard>
          ),
        },
        {
          path: 'join-subbie',
          element: (
            <AuthGuard>
              <OnboardingGuard>
                <MobileJoinSubcontractor />
              </OnboardingGuard>
            </AuthGuard>
          ),
        },
        {
          path: 'acknowledge/:jobsiteId',
          element: (
            <AuthGuard>
              <OnboardingGuard allowCheckIn>
                <MobileFirstVisitAcknowledge />
              </OnboardingGuard>
            </AuthGuard>
          ),
        },
        {
          // Live "Who's on site" roster in the field shell. Same content as
          // /dashboard/on-site; this one is a tap away from the check-in home.
          path: 'roster',
          element: (
            <AuthGuard>
              <OnboardingGuard>
                <MobileRoster />
              </OnboardingGuard>
            </AuthGuard>
          ),
        },
        {
          // Site Safety Plan + SWMS in the field shell — one tap from the
          // check-in home so a worker can show an inspector the docs.
          path: 'site-docs/:jobsiteId',
          element: (
            <AuthGuard>
              <OnboardingGuard>
                <MobileSiteDocs />
              </OnboardingGuard>
            </AuthGuard>
          ),
        },
      ],
    },

    {
      path: 'auth',
      children: [
        {
          path: 'login',
          element: (
            <GuestGuard>
              <Login />
            </GuestGuard>
          )
        },
        {
          path: 'register',
          element: (
            <GuestGuard>
              <Register />
            </GuestGuard>
          )
        },
        {
          path: 'register/:token',
          element: (
            <GuestGuard>
              <Register />
            </GuestGuard>
          )
        },
        {
          path: 'register/role/:role',
          element: (
            <GuestGuard>
              <Register />
            </GuestGuard>
          )
        },
        { path: 'user/:userId/documents/:builderId', element: <BuilderContractAccept /> },
        { path: 'login-unprotected', element: <Login /> },
        { path: 'register-unprotected', element: <Register /> },
        { path: 'reset-password/:token', element: <PasswordRecovery /> },
        { path: 'reset-password', element: <ResetPassword /> },
        { path: 'verify', element: <VerifyCode /> },
        { path: 'user/docUpdate/:requestId', element: <CardUpload /> },
        { path: 'public/user/:userId', element: <UserProfile />  }
      ]
    },

    {
      path: 'register',
      element: <RegisterRedirect />,
    },

    {
      path: 'documents/accept',
      element: <AcceptDocsToken />,
    },

    {
      path: 'store',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        { index: true, element: defaultDashboard},
        { path: 'products', element: <GiveawayProducts /> },
        { path: 'claims', element: <Claims /> },
        { path: 'settings', element: <Settings /> },
      ]
    },

    // Dashboard Routes
    {
      path: 'dashboard',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        { index: true, element: defaultDashboard},
        { path: 'app', element: defaultDashboard },
        { path: 'admin', element: <RoleBasedGuard accessibleRoles={['admin', 'superadmin']}><Navigate to="/dashboard/admin/overview" replace /></RoleBasedGuard> },
        { path: 'admin/overview', element: <RoleBasedGuard accessibleRoles={['admin', 'superadmin']}><AdminOverview /></RoleBasedGuard> },
        { path: 'admin/jobsites', element: <RoleBasedGuard accessibleRoles={['admin', 'superadmin']}><AdminJobSites /></RoleBasedGuard> },
        { path: 'admin/users', element: <RoleBasedGuard accessibleRoles={['admin', 'superadmin']}><AdminUsers /></RoleBasedGuard> },
        { path: 'admin/teams', element: <RoleBasedGuard accessibleRoles={['admin', 'superadmin']}><AdminTeams /></RoleBasedGuard> },
        { path: 'admin/teams/builder/:builderId', element: <RoleBasedGuard accessibleRoles={['admin', 'superadmin']}><AdminBuilderTeams /></RoleBasedGuard> },
        { path: 'admin/teams/:ownerUserId/members', element: <RoleBasedGuard accessibleRoles={['admin', 'superadmin']}><AdminTeamMembers /></RoleBasedGuard> },
        { path: 'admin/templates', element: <RoleBasedGuard accessibleRoles={['admin', 'superadmin']}><AdminMessageTemplates /></RoleBasedGuard> },
        { path: 'admin/templates/:slug', element: <RoleBasedGuard accessibleRoles={['admin', 'superadmin']}><AdminMessageTemplateEditor /></RoleBasedGuard> },
        { path: 'admin/document-templates', element: <RoleBasedGuard accessibleRoles={['admin', 'superadmin']}><AdminBusinessTypeRequirements /></RoleBasedGuard> },
        { path: 'admin/subscription-requests', element: <RoleBasedGuard accessibleRoles={['admin', 'superadmin']}><AdminSubscriptionRequests /></RoleBasedGuard> },
        { path: 'campaigns', element: <CampaignsGuard><Campaigns /></CampaignsGuard> },
        { path: 'campaigns/new', element: <CampaignsGuard><CampaignEditor /></CampaignsGuard> },
        { path: 'campaigns/:id', element: <CampaignsGuard><CampaignEditor /></CampaignsGuard> },
        { path: 'dashBuilder', element: <DashBuilderMain /> },
        { path: 'overview', element: <OnboardingGuard><DashboardOverview /></OnboardingGuard> },
        { path: 'on-site', element: <OnboardingGuard><DashboardOnSiteNow /></OnboardingGuard> },
        // The register modules. Behind OnboardingGuard like every other
        // module: they are organisation-scoped, and an account that has not
        // finished onboarding has no organisation to scope them to.
        { path: 'incidents', element: <OnboardingGuard><DashboardIncidents /></OnboardingGuard> },
        { path: 'incidents/:id', element: <OnboardingGuard><DashboardIncidentDetail /></OnboardingGuard> },
        { path: 'actions', element: <OnboardingGuard><DashboardActions /></OnboardingGuard> },
        { path: 'actions/:id', element: <OnboardingGuard><DashboardActionDetail /></OnboardingGuard> },
        { path: 'equipment', element: <OnboardingGuard><DashboardEquipment /></OnboardingGuard> },
        { path: 'equipment/:id', element: <OnboardingGuard><DashboardEquipmentDetail /></OnboardingGuard> },
        { path: 'risks', element: <OnboardingGuard><DashboardRisks /></OnboardingGuard> },
        { path: 'risks/:id', element: <OnboardingGuard><DashboardRiskDetail /></OnboardingGuard> },
        { path: 'inspections', element: <OnboardingGuard><DashboardInspections /></OnboardingGuard> },
        { path: 'inspections/:id', element: <OnboardingGuard><DashboardInspectionDetail /></OnboardingGuard> },
        { path: 'inspections/checklists/:id', element: <OnboardingGuard><DashboardChecklistDetail /></OnboardingGuard> },
        { path: 'training', element: <OnboardingGuard><DashboardTraining /></OnboardingGuard> },
        { path: 'noticeboard', element: <OnboardingGuard><DashboardNoticeboard /></OnboardingGuard> },
        { path: 'chemicals', element: <OnboardingGuard><DashboardChemicals /></OnboardingGuard> },
        { path: 'chemicals/:id', element: <OnboardingGuard><DashboardChemicalDetail /></OnboardingGuard> },
        { path: 'tasks', element: <OnboardingGuard><DashboardTasks /></OnboardingGuard> },
        { path: 'document-requirements', element: <OnboardingGuard><DashboardDocRequirements /></OnboardingGuard> },
        { path: 'plans', element: <OnboardingGuard><DashboardSubscription /></OnboardingGuard> },
        { path: 'plans/choose', element: <OnboardingGuard><DashboardPlans /></OnboardingGuard> },
        { path: 'checklist', element: <DashBuilderMain /> },
        { path: 'builder', element: <DashBuilder /> },
       
        { path: 'employee', element: <DashEmployee /> },
        { path: 'subcontractor', element: <DashSubcontractors /> },
        { path: 'officeadmin', element: <DashOfficeadmin /> },
        { path: 'supervisor', element: <DashSupervisor /> },
        { path: 'storeAdmin', element: <DashStoreAdmin /> },
        { path: 'jobsites', element: <JobSitesMap /> },
        { path: 'jobsites/:jobsiteId', element: <JobDetails /> },
        { path: 'jobsites/add', element: <AddJobSite /> },
        { path: 'jobsites/:jobsiteId/edit', element: <UpdateJobsite /> },
        { path: 'api-keys', element: <ApiKeys /> },
        // F-5 membership flows. Both sit on the dashboard layout; RBAC is
        // enforced server-side, and the existing menu can link to them as
        // builder/subbie-specific entries.
        { path: 'builder/attach-worker', element: <AttachWorkerToSubbie /> },
        { path: 'subcontractor/requests', element: <PendingRequests /> },
        {
          path: 'documents',
          // element:  <Documents type="ptc" />,
          children: [
            { index: true, element: <Documents type="ptc" />},
            // Builder-facing full-page editor for their own PTC/WHS/WIR.
            { path: 'my/:type', element: <MyDocument /> },
            { path: 'ptc', element: <Documents type="ptc" /> },
            { path: 'whs', element: <Documents type="whs" /> },
            { path: 'wir', element: <Documents type="wir" /> },
            { path: 'swms', element: <Swms /> },
            { path: 'hrwa', element: <Hrwa /> },
            { path: 'hazardousSubstances', element: <HazardousSubstances /> },
            { path: 'termsAndConditions', element: <TermsAndConditions /> },

          ]
        },
        {
          path: 'documents',
          // element:  <Documents type="ptc" />,
          children: [
            { path: 'old', element: <OldDocuments type="ptc" />},
            { path: 'old/ptc', element: <OldDocuments type="ptc" /> },
            { path: 'old/whs', element: <OldDocuments type="whs" /> },
            { path: 'old/wir', element: <OldDocuments type="wir" /> }

          ]
        },
        {
          path: 'verification',
          children: [
            { index: true, element: <Verification type="cards" />},
            { path: 'cards', element: <Verification type="cards" />},
            { path: 'licenses', element: <Verification type="licenses" />},
            { path: 'insurances', element: <Verification type="insurances" />},
            { path: 'workCovers', element: <Verification type="workCovers" />},
            { path: 'ewpCards', element: <Verification type="ewpCards" />},
            { path: 'workingHeightsCards', element: <Verification type="workingHeightsCards" />},
            { path: 'testTag', element: <Verification type="testTag" />},


          ]
        },
        {
          path: 'jobsiteSWMS',
          children: [
            { index: true, element: <JobsiteSwms />},
            { path: 'constructionTypes', element: <ConstructionTypeSwms /> },
            { path: 'poolTypes', element: <PoolTypeSwms /> },
          ]
        },
        {
          path: 'analytics',
          element: <GeneralAnalytics />
        },
        {
          path: 'user',
          children: [
            { index: true, element: <Navigate to="/dashboard/user/profile" replace /> },
            { path: 'profile', element: <UserProfile /> },
            { path: 'profile/update', element: <CardUpload /> },
            { path: 'profile/:actionType/:docType', element: <UserProfile /> },
            { path: 'profile/:userId', element: <UserProfile /> },
            { path: 'cards', element: <UserCards /> },
            { path: 'list', element: <UserList /> },
            {
              path: 'yourteam',
              // Employees have no team-management view — keep them out of the
              // route even via a direct URL (backend 403s the data too).
              element: (
                <RoleBasedGuard
                  accessibleRoles={['builder', 'subcontractor', 'officeadmin', 'supervisor', 'admin', 'superadmin']}
                >
                  <YourTeam />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'documents',
              element: (
                <RoleBasedGuard
                  accessibleRoles={['builder', 'subcontractor', 'officeadmin', 'supervisor', 'admin', 'superadmin']}
                >
                  <DocumentRegister />
                </RoleBasedGuard>
              ),
            },
            { path: 'mycompanies', element: <MyCompanies /> },
            { path: 'new', element: <UserCreate /> },
            { path: ':name/edit', element: <UserCreate /> },
            { path: 'account', element: <UserAccount /> },
			      { path: 'upload/:docType/card/:id', element: <CardUpload /> }
          ]
        },
      ]
    },
    // OnBoard Routes -- This is the page where users 1st see when they login and will show the onboard steps
    {
      path: 'onboarding',
      element: (
        <AuthGuard>
          <OnboardLayout />
        </AuthGuard>
      ),
    },

    // Main Routes
    {
      path: '*',
      element: <LogoOnlyLayout />,
      children: [
        { path: 'coming-soon', element: <ComingSoon /> },
        { path: 'maintenance', element: <Maintenance /> },
        { path: '500', element: <Page500 /> },
        { path: '404', element: <NotFound /> },
        { path: '*', element: <Navigate to="/404" replace /> }
      ]
    },
    {
      path: '/',
      element: <MainLayout />,
      children: [
        // The home page, not a redirect to the login form. Every layout's logo
        // links to '/', so while this bounced to /auth/login, clicking the logo
        // threw people — including signed-in ones — at a sign-in screen.
        { index: true, element: <LandingPage /> },
        { path: 'pricing', element: <PricingPage /> },
        { path: 'TermsOfService', element: <TermsOfService /> },
        { path: 'PrivacyPolicy', element: <PrivacyPolicy /> }
      ]
    },
    { path: '*', element: <Navigate to="/404" replace /> }
  ]);
}

// IMPORT COMPONENTS

// Mobile / field PWA. All lazy — keeps mobile chunks small.
const MobileHome = Loadable(lazy(() => import('../pages/mobile/Home')));
const MobileSignOnConfirm = Loadable(lazy(() => import('../pages/mobile/SignOnConfirm')));
const MobileSignOffConfirm = Loadable(lazy(() => import('../pages/mobile/SignOffConfirm')));
const MobileSwitchSitesConfirm = Loadable(lazy(() => import('../pages/mobile/SwitchSitesConfirm')));
const MobileLoginAtSite = Loadable(lazy(() => import('../pages/mobile/LoginAtSite')));
const MobileRegisterAtSite = Loadable(lazy(() => import('../pages/mobile/RegisterAtSite')));
const MobilePickEmployer = Loadable(lazy(() => import('../pages/mobile/PickEmployer')));
const MobileJoinSubcontractor = Loadable(lazy(() => import('../pages/mobile/JoinSubcontractor')));
const MobileFirstVisitAcknowledge = Loadable(
  lazy(() => import('../pages/mobile/FirstVisitAcknowledge'))
);
const MobileRoster = Loadable(lazy(() => import('../pages/mobile/Roster')));
const MobileSiteDocs = Loadable(lazy(() => import('../pages/mobile/SiteDocs')));
const AttachWorkerToSubbie = Loadable(lazy(() => import('../pages/dashboard/AttachWorkerToSubbie')));
const PendingRequests = Loadable(lazy(() => import('../pages/dashboard/PendingRequests')));

// Authentication
const Login = Loadable(lazy(() => import('../pages/authentication/Login')));
const Register = Loadable(lazy(() => import('../pages/authentication/Register')));
const ResetPassword = Loadable(lazy(() => import('../pages/authentication/ResetPassword')));
const PasswordRecovery = Loadable(lazy(() => import('../pages/authentication/PasswordRecovery')));
const VerifyCode = Loadable(lazy(() => import('../pages/authentication/VerifyCode')));
// Onboard Layout
const OnboardLayout = Loadable(lazy(() => import('../pages/onboard/OnBoardLayout')));

// Dashboard
const Documents = Loadable(lazy(() => import('../pages/documents/index')));
const MyDocument = Loadable(lazy(() => import('../pages/documents/MyDocument')));
const TermsAndConditions = Loadable(lazy(() => import('../pages/documents/TermsAndConditions')));
const GiveawayProducts = Loadable(lazy(() => import('../pages/store/GiveawayProducts')));
const Claims = Loadable(lazy(() => import('../pages/store/Claims')));
const Settings = Loadable(lazy(() => import('../pages/store/Settings')));
const OldDocuments = Loadable(lazy(() => import('../pages/documents/indexCopy')));
const Verification = Loadable(lazy(() => import('../pages/verification/index')));
const JobsiteSwms = Loadable(lazy(() => import('../pages/jobsite-swms/index')));
const ConstructionTypeSwms = Loadable(lazy(() => import('../pages/jobsite-swms/constructionTypes')));
const PoolTypeSwms = Loadable(lazy(() => import('../pages/jobsite-swms/poolTypes')));
// const Swms = Loadable(lazy(() => import('../pages/documents/index')));
const GeneralAnalytics = Loadable(lazy(() => import('../pages/dashboard/GeneralAnalytics')));
const DashBuilder = Loadable(lazy(() => import('../pages/dashboard/GeneralDashBuilder')));
const DashBuilderMain = Loadable(lazy(() => import('../pages/dashboard/BuildersDash')));
const DashboardOverview = Loadable(lazy(() => import('../pages/dashboard/Overview')));
const DashboardOnSiteNow = Loadable(lazy(() => import('../pages/dashboard/OnSiteNow')));
const DashboardIncidents = Loadable(lazy(() => import('../pages/dashboard/Incidents')));
const DashboardActions = Loadable(lazy(() => import('../pages/dashboard/Actions')));
const DashboardEquipment = Loadable(lazy(() => import('../pages/dashboard/Equipment')));
const DashboardRisks = Loadable(lazy(() => import('../pages/dashboard/RiskRegister')));
const DashboardInspections = Loadable(lazy(() => import('../pages/dashboard/Inspections')));
const DashboardTraining = Loadable(lazy(() => import('../pages/dashboard/Training')));
const DashboardNoticeboard = Loadable(lazy(() => import('../pages/dashboard/Noticeboard')));
const DashboardChemicals = Loadable(lazy(() => import('../pages/dashboard/Chemicals')));
const DashboardIncidentDetail = Loadable(lazy(() => import('../pages/dashboard/IncidentDetail')));
const DashboardActionDetail = Loadable(lazy(() => import('../pages/dashboard/ActionDetail')));
const DashboardEquipmentDetail = Loadable(lazy(() => import('../pages/dashboard/EquipmentDetail')));
const DashboardRiskDetail = Loadable(lazy(() => import('../pages/dashboard/RiskDetail')));
const DashboardInspectionDetail = Loadable(lazy(() => import('../pages/dashboard/InspectionDetail')));
const DashboardChecklistDetail = Loadable(lazy(() => import('../pages/dashboard/ChecklistDetail')));
const DashboardChemicalDetail = Loadable(lazy(() => import('../pages/dashboard/ChemicalDetail')));
const DashboardTasks = Loadable(lazy(() => import('../pages/dashboard/Tasks')));
const DashboardDocRequirements = Loadable(lazy(() => import('../pages/dashboard/DocumentRequirements')));
const DashboardPlans = Loadable(lazy(() => import('../pages/dashboard/Plans')));
const DashboardSubscription = Loadable(lazy(() => import('../pages/dashboard/Subscription')));
const AdminBusinessTypeRequirements = Loadable(lazy(() => import('../pages/dashboard/admin/AdminBusinessTypeRequirements')));
const AdminSubscriptionRequests = Loadable(lazy(() => import('../pages/dashboard/admin/AdminSubscriptionRequests')));
const DashEmployee = Loadable(lazy(() => import('../pages/dashboard/GeneralDashEmployee')));
const DashSubcontractors = Loadable(lazy(() => import('../pages/dashboard/GeneralDashSubcontractors')));
const DashOfficeadmin = Loadable(lazy(() => import('../pages/dashboard/GeneralDashOfficeadmin')));
const DashSupervisor = Loadable(lazy(() => import('../pages/dashboard/GeneralDashSupervisor')));
const DashStoreAdmin = Loadable(lazy(() => import('../pages/dashboard/GeneralDashStoreAdmin')));
const UserProfile = Loadable(lazy(() => import('../pages/dashboard/UserProfile')));
const UserCards = Loadable(lazy(() => import('../pages/dashboard/UserCards')));
const UserList = Loadable(lazy(() => import('../pages/dashboard/UserList')));
const DocumentRegister = Loadable(lazy(() => import('../pages/dashboard/DocumentRegister')));
const CardUpload = Loadable(lazy(() => import('../pages/dashboard/CardUpload')));
const BuilderContractAccept = Loadable(lazy(() => import('../pages/dashboard/BuilderContractAccept')));
const AcceptDocsToken = Loadable(lazy(() => import('../pages/documents/AcceptDocsToken')));
const YourTeam = Loadable(lazy(() => import('../pages/dashboard/YourTeam')));
const MyCompanies = Loadable(lazy(() => import('../pages/dashboard/MyCompanies')));
const UserAccount = Loadable(lazy(() => import('../pages/dashboard/UserAccount')));
const AddJobSite = Loadable(lazy(() => import('../pages/dashboard/AddJobSite')));
const UpdateJobsite = Loadable(lazy(() => import('../pages/dashboard/AddJobSite')));
const JobDetails = Loadable(lazy(() => import('../pages/dashboard/JobDetails')));
const ApiKeys = Loadable(lazy(() => import('../pages/dashboard/ApiKeys')));
const UserCreate = Loadable(lazy(() => import('../pages/dashboard/UserCreate')));
const JobSitesMap = Loadable(lazy(() => import('../pages/dashboard/JobSitesMap')));
const AdminOverview = Loadable(lazy(() => import('../pages/dashboard/admin/AdminOverview')));
const AdminJobSites = Loadable(lazy(() => import('../pages/dashboard/admin/AdminJobSites')));
const AdminUsers = Loadable(lazy(() => import('../pages/dashboard/admin/AdminUsers')));
const AdminTeams = Loadable(lazy(() => import('../pages/dashboard/admin/AdminTeams')));
const AdminBuilderTeams = Loadable(lazy(() => import('../pages/dashboard/admin/AdminBuilderTeams')));
const AdminTeamMembers = Loadable(lazy(() => import('../pages/dashboard/admin/AdminTeamMembers')));
const AdminMessageTemplates = Loadable(lazy(() => import('../pages/dashboard/admin/AdminMessageTemplates')));
const AdminMessageTemplateEditor = Loadable(lazy(() => import('../pages/dashboard/admin/AdminMessageTemplateEditor')));
const Campaigns = Loadable(lazy(() => import('../pages/dashboard/Campaigns')));
const CampaignEditor = Loadable(lazy(() => import('../pages/dashboard/CampaignEditor')));
// Main
const JobsiteDocuments = Loadable(lazy(() => import('../pages/JobsiteDocuments')));
const SharedSiteDocs = Loadable(lazy(() => import('../pages/SharedSiteDocs')));
const TermsOfService = Loadable(lazy(() => import('../pages/TermsOfService')));
const LandingPage = Loadable(lazy(() => import('../pages/LandingPage')));
const PricingPage = Loadable(lazy(() => import('../pages/Pricing')));
const PrivacyPolicy = Loadable(lazy(() => import('../pages/PrivacyPolicy')));
const ComingSoon = Loadable(lazy(() => import('../pages/ComingSoon')));
const Maintenance = Loadable(lazy(() => import('../pages/Maintenance')));
const Page500 = Loadable(lazy(() => import('../pages/Page500')));
const NotFound = Loadable(lazy(() => import('../pages/Page404')));